import { Dispatch } from 'redux';
import { config } from '../config';
import { ApplicationActions, RootApplicationAction, AccountApplicationWebSocketActions } from '../state/actions';
import { AccountApplicationUpdate, FeedData } from '@tradingblock/types';
import { WebSocketStatuses } from '../types/WebSocket';
import { DataStore } from '../state/store';

class AccountApplicationWebSocketService {
  private static instance: AccountApplicationWebSocketService;
  private ws: WebSocket | null = null;
  private constructor() {}

  // Singleton instance access
  public static getInstance(): AccountApplicationWebSocketService {
    if (!AccountApplicationWebSocketService.instance) {
      AccountApplicationWebSocketService.instance = new AccountApplicationWebSocketService();
    }

    return AccountApplicationWebSocketService.instance;
  }

  // Initialize WebSocket connection
  public async connect(dispatch: Dispatch<RootApplicationAction>, apiToken: string) {
    try {
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
        this.ws = new WebSocket(config.accountApplicationWebSocketUrl + '?token=' + apiToken);

        this.ws.onopen = () => {
          dispatch(AccountApplicationWebSocketActions.setStatus(WebSocketStatuses.OPEN));
        };

        this.ws.onmessage = (event: MessageEvent) => {
          const jsonVal = event.data;
          const accountApplicationUpdateData: FeedData<AccountApplicationUpdate> = JSON.parse(jsonVal);
          const reduxState = DataStore.getState();

          if (!reduxState.accountApplicationWebSocket.hasReceivedAccountApplicationUpdateMessage) {
            dispatch(AccountApplicationWebSocketActions.setHasReceivedAccountApplicationUpdateMessage(true));
          }

          if (
            !accountApplicationUpdateData.investigationId &&
            ((reduxState.data.status &&
              reduxState.data.status.state !== accountApplicationUpdateData.accountApplicationStatus) ||
              !reduxState ||
              !reduxState.data ||
              !reduxState.data.status)
          ) {
            dispatch(
              ApplicationActions.requestApplicationStatus({ accountId: accountApplicationUpdateData.accountId })
            );
          }
        };

        this.ws.onclose = (event: CloseEvent) => {
          if (event.reason) {
            console.warn(event.reason);
          }

          dispatch(AccountApplicationWebSocketActions.setStatus(WebSocketStatuses.CLOSED));
        };

        this.ws.onerror = error => {
          dispatch(AccountApplicationWebSocketActions.setStatus(WebSocketStatuses.ERROR));

          console.error('WebSocket error: ', error);
        };
      }
    } catch (error) {
      dispatch(AccountApplicationWebSocketActions.setStatus(WebSocketStatuses.ERROR));

      console.error('WebSocket creation error: ', error);
    }
  }

  // Send message to WebSocket
  public sendMessage(data: string | ArrayBufferLike | Blob | ArrayBufferView): { success: boolean } {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);

      return { success: true };
    } else if (this.ws) {
      console.warn('Trying to send message to closed/closing WebSocket: ', data);
    }

    return { success: false };
  }

  public shouldTryReconnect() {
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      return true;
    }

    return false;
  }

  // Close WebSocket connection
  public closeConnection() {
    if (this.ws) {
      this.ws.close();
    }
  }
}

export const accountApplicationWebSocketService = AccountApplicationWebSocketService.getInstance();
